import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Image, Dimensions, ActivityIndicator } from 'react-native';
import { useNavigation, useRoute, RouteProp, CompositeNavigationProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { WorkStackParamList, RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { Job } from '../../types/work';
import { getRelativeTime } from '../../data/mockWorkData';
import { Alert } from '../../utils/toast';
import ThemedText from '../../components/common/ThemedText';
import { shareItem } from '../../utils/share';

type ProductDetailNavigationProp = CompositeNavigationProp<
  StackNavigationProp<WorkStackParamList, 'ProductDetail'>,
  StackNavigationProp<RootStackParamList>
>;
type ProductDetailRouteProp = RouteProp<WorkStackParamList, 'ProductDetail'>;

const { width } = Dimensions.get('window');

const ProductDetailScreen: React.FC = () => {
  const navigation = useNavigation<ProductDetailNavigationProp>();
  const route = useRoute<ProductDetailRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { fetchJobById, deleteJob } = useWork();
  const { conversations, createConversation } = useChat();
  const { productId } = route.params;
  
  const [product, setProduct] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await fetchJobById(productId);
      setProduct(data);
      console.log('[ProductDetail] User role:', user?.role, 'User ID:', user?.userId, 'Product creator:', data?.createdBy?.id);
    } catch (error) {
      Alert.alert('Error', 'Failed to load product details');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleContactSeller = async () => {
    if (!product?.createdBy) return;
    
    try {
      const price = product.price || product.budget;
      const priceText = price ? `$${price.toLocaleString()}` : 'the listed price';
      const initialMessage = `Hi! I'm interested in your ${product.title}. Is it still available for ${priceText}?`;
      
      // Find existing conversation with this user
      const existingConversation = conversations.find(conv => 
        conv.type === 'personal' && 
        conv.participants.some(p => p.id === product.createdBy!.id)
      );
      
      if (existingConversation) {
        // Navigate to existing conversation with initial message
        navigation.navigate('PersonalChat', {
          userId: product.createdBy.id,
          userName: product.createdBy.name || 'Seller',
          conversationId: existingConversation.id,
          initialMessage,
        });
      } else {
        // Create new conversation first
        const newConversation = await createConversation(product.createdBy.id);
        navigation.navigate('PersonalChat', {
          userId: product.createdBy.id,
          userName: product.createdBy.name || 'Seller',
          conversationId: newConversation.id,
          initialMessage,
        });
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      Alert.alert('Error', 'Failed to open chat. Please try again.');
    }
  };

  const handleViewProfile = (userId: string) => {
    navigation.navigate('Profile', { userId });
  };

  const handleEdit = () => {
    navigation.navigate('EditProduct', { productId });
  };

  const handleDelete = async () => {
    Alert.alert(
      'Delete Product',
      'Are you sure you want to delete this product listing?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteJob(productId);
              Alert.alert('Success', 'Product deleted successfully', [
                { text: 'OK', onPress: () => navigation.goBack() }
              ]);
            } catch (error) {
              Alert.alert('Error', 'Failed to delete product. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleShare = async () => {
    if (!product) return;
    
    await shareItem({
      id: productId,
      title: product.title,
      type: 'product',
      description: product.description,
      price: product.price,
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Product Details</ThemedText>
          <View style={styles.backButton} />
        </View>
        <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!product) {
    return null;
  }

  const isOwner = product.createdBy?.id === user?.userId;
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const canDelete = isOwner || isAdmin;
  
  console.log('[ProductDetail] isOwner:', isOwner, 'isAdmin:', isAdmin, 'canDelete:', canDelete);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={styles.headerTitle}>Product Details</ThemedText>
        <View style={styles.headerActions}>
          {isOwner && (
            <TouchableOpacity onPress={handleEdit} style={styles.headerActionButton}>
              <Ionicons name="create-outline" size={24} color={theme.text} />
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleShare} style={styles.headerActionButton}>
            <Ionicons name="share-outline" size={24} color={theme.text} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Product Image */}
        {product.images && product.images.length > 0 ? (
          <Image 
            source={{ uri: product.images[0] }} 
            style={styles.productImage}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.placeholderImage, { backgroundColor: theme.backgroundSecondary }]}>
            <Ionicons name="image-outline" size={80} color={theme.textTertiary} />
          </View>
        )}

        <View style={styles.infoSection}>
          {/* Category & Time */}
          <View style={styles.metaRow}>
            <View style={styles.categoryBadge}>
              <Ionicons name="pricetag" size={14} color={theme.primary} />
              <ThemedText style={[styles.categoryText, { color: theme.primary }]}>
                {product.category}
              </ThemedText>
            </View>
            <View style={styles.timeContainer}>
              <Ionicons name="time-outline" size={14} color={theme.textTertiary} />
              <ThemedText style={[styles.timeText, { color: theme.textTertiary }]}>
                {getRelativeTime(product.createdAt)}
              </ThemedText>
            </View>
          </View>

          {/* Title */}
          <ThemedText style={[styles.title, { color: theme.text }]}>
            {product.title}
          </ThemedText>

          {/* Price */}
          <LinearGradient
            colors={[theme.primary + '15', theme.primary + '08']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.pricePill}
          >
            <Ionicons name="cash-outline" size={20} color={theme.primary} />
            <ThemedText style={[styles.priceText, { color: theme.primary }]}>
              ${product.price?.toLocaleString()}
            </ThemedText>
          </LinearGradient>

          {/* Divider */}
          <View style={[styles.divider, { backgroundColor: theme.border }]} />

          {/* Description */}
          <View style={styles.section}>
            <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Description</ThemedText>
            <ThemedText style={[styles.description, { color: theme.textSecondary }]}>
              {product.description}
            </ThemedText>
          </View>

          {/* Seller Info - Only show if not owner */}
          {!isOwner && (
            <View style={styles.section}>
              <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Seller Information</ThemedText>
              <TouchableOpacity 
                style={[styles.sellerCard, { backgroundColor: theme.backgroundSecondary }]}
                onPress={() => handleViewProfile(product.createdBy?.id || '')}
                activeOpacity={0.7}
              >
                {product.createdBy?.photo ? (
                  <Image source={{ uri: product.createdBy.photo }} style={styles.sellerAvatar} />
                ) : (
                  <View style={[styles.sellerAvatar, { backgroundColor: theme.primary + '20' }]}>
                    <Ionicons name="person" size={24} color={theme.primary} />
                  </View>
                )}
                <View style={styles.sellerInfo}>
                  <ThemedText style={[styles.sellerName, { color: theme.text }]}>
                    {product.createdBy?.name || 'Unknown'}
                  </ThemedText>
                  <ThemedText style={[styles.sellerMeta, { color: theme.textSecondary }]}>
                    Seller
                  </ThemedText>
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Delete Button - Show for owner or admin */}
          {canDelete && (
            <View style={styles.section}>
              <TouchableOpacity 
                style={[styles.deleteButton, { backgroundColor: theme.error + '10', borderColor: theme.error }]}
                onPress={handleDelete}
              >
                <Ionicons name="trash-outline" size={20} color={theme.error} />
                <ThemedText style={[styles.deleteButtonText, { color: theme.error }]}>
                  Delete Product {isAdmin && !isOwner && '(Admin)'}
                </ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Footer Button - Contact Seller for non-owners only */}
      {!isOwner && (
        <View style={[styles.footer, { backgroundColor: theme.background, borderTopColor: theme.border }]}>
          <TouchableOpacity 
            style={[styles.contactButton, { backgroundColor: theme.accent }]}
            onPress={handleContactSeller}
          >
            <Ionicons name="chatbubble-ellipses" size={20} color={theme.textInverse} />
            <ThemedText style={[styles.contactButtonText, { color: theme.textInverse }]}>
              Contact Seller
            </ThemedText>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerActionButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  productImage: {
    width: width,
    height: width,
  },
  placeholderImage: {
    width: width,
    height: width,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoSection: {
    padding: 20,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(196, 150, 62, 0.1)',
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  timeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  timeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 32,
    marginBottom: 16,
    letterSpacing: -0.5,
  },
  pricePill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 20,
  },
  priceText: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    marginVertical: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
  sellerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
  },
  sellerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  sellerInfo: {
    flex: 1,
  },
  sellerName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  sellerMeta: {
    fontSize: 14,
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    gap: 12,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  contactButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 12,
  },
  contactButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});

export default ProductDetailScreen;
